/** @jsxImportSource @emotion/react */
import { createContext, Dispatch, SetStateAction, useContext, useState } from 'react'
import { useDispatch } from 'react-redux'

import { type PrioritisationMapLayer } from '@app/contexts/ModuleContexts/PrioritisationContext'
import {
  updateMapStateProperty,
  updateSelectedFeature,
} from '@app/redux/prioritisationMap/prioritisationMapSlice'
import { MapLayer } from '@app/redux/riskMap/riskMapSlice'
import { removeSelectedMarkerFromMapIfExists } from '@app/components/Molecules/MapView/GenericMapView/GenericMapView.utilities'
import { useGenericMapContext } from '../GenericMapContext'
import {
  ClearMapProps,
  GenericMapContextInterface,
  MapProviderProps,
} from '../GenericMapContext/GenericMapContext.types'

export interface PrioritisationMapContext extends GenericMapContextInterface {
  selectedPrioLayer: PrioritisationMapLayer | null
  setSelectedPrioLayer: Dispatch<SetStateAction<PrioritisationMapLayer | null>>
  selectFeature: (feature: mapboxgl.MapboxGeoJSONFeature, layer: MapLayer) => boolean
  deselectFeature: () => void
}

export const PrioritisationMapContext = createContext({} as PrioritisationMapContext)

export function PrioritisationMapProvider({ children }: MapProviderProps) {
  const dispatch = useDispatch()
  const tab = 'Risk'
  const genericMapContext = useGenericMapContext()
  const [selectedPrioLayer, setSelectedPrioLayer] = useState<PrioritisationMapLayer | null>(null)

  const clearMap = ({ map }: ClearMapProps) => {
    if (!map) return

    genericMapContext.clearMap({ map })
    dispatch(updateMapStateProperty({ layers: [] }))
    deselectFeature()
  }

  const deselectFeature = () => {
    dispatch(
      updateSelectedFeature({
        selectedFeature: null,
        selectedLayer: null,
      }),
    )
    dispatch(updateMapStateProperty({ selectedMarker: null }))
    removeSelectedMarkerFromMapIfExists()
  }

  const selectFeature = (feature: mapboxgl.MapboxGeoJSONFeature, layer: MapLayer) => {
    if (!genericMapContext.map || tab !== 'Risk') return false
    dispatch(
      updateSelectedFeature({
        selectedFeature: feature,
        selectedLayer: layer,
      }),
    )

    return true
  }

  return (
    <PrioritisationMapContext.Provider
      value={{
        ...genericMapContext,
        selectedPrioLayer,
        setSelectedPrioLayer,
        selectFeature,
        deselectFeature,
        clearMap,
      }}
    >
      {children}
    </PrioritisationMapContext.Provider>
  )
}

export const usePrioritisationMap = () => useContext(PrioritisationMapContext)
